/**
 * @file ChefStatsModal.tsx
 * @description Modal dialog presenting kitchen production turnaround KPIs and dish leaderboards.
 */

import React from 'react';
import { X, BarChart3 } from 'lucide-react';
import { ChefProductionSummary } from './ChefProductionSummary';
import { useChefStats } from '../hooks/useChefStats';

interface ChefStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueId: string | null;
}

export const ChefStatsModal: React.FC<ChefStatsModalProps> = ({ isOpen, onClose, venueId }) => {
  const { stats, timeframe, setTimeframe, isLoading } = useChefStats(venueId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90dvh] pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-0">
        {/* Mobile slide-up drag handle */}
        <div className="sm:hidden w-full flex justify-center pt-2.5 pb-1 bg-zinc-900/60 shrink-0">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/50 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 pr-2">
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 shrink-0">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-black text-white truncate">CHEF PRODUCTION PERFORMANCE</h2>
              <p className="text-xs text-zinc-400 truncate">Preparation speed, turnaround metrics, and dish output</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <ChefProductionSummary
            stats={stats}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
            isLoading={isLoading}
          />
        </div>

        <div className="p-4 border-t border-zinc-800 bg-zinc-900/40 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 min-h-[46px] rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-bold text-sm active:scale-[0.98] transition-all cursor-pointer"
          >
            Close Performance KPIs
          </button>
        </div>
      </div>
    </div>
  );
};
